import { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { X, Zap, CreditCard, Clock } from 'lucide-react';
import { stripeProducts, type StripeProductKey } from '../stripe-config';
import { formatPrice } from '../lib/currency';

interface TankControlModalProps {
  onClose: () => void;
}

export default function TankControlModal({ onClose }: TankControlModalProps) {
  const { i18n, t } = useTranslation();
  const [selectedPackage, setSelectedPackage] = useState<StripeProductKey | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const getTranslatedText = (key: string) => {
    const texts: { [key: string]: { [lang: string]: string } } = {
      title: {
        cs: 'Ovládání tanku',
        en: 'Tank Control',
        de: 'Panzer-Steuerung',
        sk: 'Ovládanie tanku'
      },
      description: {
        cs: 'Získejte exkluzivní přístup k ovládání našeho skutečného tanku na dálku! Zažijte jedinečný zážitek z ovládání vojenské techniky přímo z vašeho prohlížeče.',
        en: 'Get exclusive access to control our real tank remotely! Experience the unique thrill of operating military technology directly from your browser.',
        de: 'Erhalten Sie exklusiven Zugang zur Fernsteuerung unseres echten Panzers! Erleben Sie den einzigartigen Nervenkitzel beim Bedienen von Militärtechnik direkt von Ihrem Browser aus.',
        sk: 'Získajte exkluzívny prístup k ovládaniu nášho skutočného tanku na diaľku! Zažite jedinečný zážitok z ovládania vojenskej techniky priamo z vášho prehliadača.'
      },
      controlNow: {
        cs: 'Ovládat tank nyní',
        en: 'Control tank now',
        de: 'Panzer jetzt steuern',
        sk: 'Ovládať tank teraz'
      },
      selectPackage: {
        cs: 'Vyberte balíček',
        en: 'Select package',
        de: 'Paket auswählen',
        sk: 'Vyberte balíček'
      },
      securePayment: {
        cs: 'Bezpečné platby přes Stripe',
        en: 'Secure payments via Stripe',
        de: 'Sichere Zahlungen über Stripe',
        sk: 'Bezpečné platby cez Stripe'
      },
      payNow: {
        cs: 'Zaplatit nyní',
        en: 'Pay now',
        de: 'Jetzt bezahlen',
        sk: 'Zaplatiť teraz'
      },
      cancel: {
        cs: 'Zrušit',
        en: 'Cancel',
        de: 'Abbrechen',
        sk: 'Zrušiť'
      },
      package5min: {
        cs: '5 minut ovládání',
        en: '5 minutes control',
        de: '5 Minuten Steuerung',
        sk: '5 minút ovládania'
      },
      package5minDesc: {
        cs: 'Základní balíček pro vyzkoušení',
        en: 'Basic package to try it out',
        de: 'Basis-Paket zum Ausprobieren',
        sk: 'Základný balíček na vyskúšanie'
      },
      package15min: {
        cs: '15 minut ovládání',
        en: '15 minutes control',
        de: '15 Minuten Steuerung',
        sk: '15 minút ovládania'
      },
      package15minDesc: {
        cs: 'Populární balíček pro delší zážitek',
        en: 'Popular package for longer experience',
        de: 'Beliebtes Paket für längere Erfahrung',
        sk: 'Populárny balíček pre dlhší zážitok'
      },
      package30min: {
        cs: '30 minut ovládání',
        en: '30 minutes control',
        de: '30 Minuten Steuerung',
        sk: '30 minút ovládania'
      },
      package30minDesc: {
        cs: 'Prémiový balíček pro maximální zážitek',
        en: 'Premium package for maximum experience',
        de: 'Premium-Paket für maximale Erfahrung',
        sk: 'Prémiový balíček pre maximálny zážitok'
      }
    };

    return texts[key]?.[i18n.language] || texts[key]?.cs || key;
  };

  const packages = [
    {
      key: 'tankControl5min' as StripeProductKey,
      duration: 5,
      price: 25,  // CZK
      popular: false
    },
    {
      key: 'tankControl15min' as StripeProductKey,
      duration: 15,
      price: 60,  // CZK
      popular: true
    },
    {
      key: 'tankControl30min' as StripeProductKey,
      duration: 30,
      price: 100, // CZK
      popular: false
    }
  ];

  const handlePayment = async () => {
    if (!selectedPackage) return;

    // For 5-minute package, redirect directly to Stripe payment link
    if (selectedPackage === 'tankControl5min') {
      window.location.href = 'https://buy.stripe.com/6oU5kCbF92Qw8CY0DoaAw00';
      return;
    }

    try {
      setIsProcessing(true);
      
      const product = stripeProducts[selectedPackage];
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-checkout`;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          price_id: product.priceId,
          mode: product.mode,
          success_url: `${window.location.origin}/tank-success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${window.location.origin}/`
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const { url } = await response.json();
      
      if (url) {
        window.location.href = url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Payment error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-gray-900 border border-[#EE1C23]/30 rounded-2xl p-8 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Zap className="w-8 h-8 text-[#EE1C23]" />
            <h2 className="text-2xl font-bold text-white font-orbitron">
              {getTranslatedText('title')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <p className="text-gray-300 text-sm mb-6 leading-relaxed">
          {getTranslatedText('description')}
        </p>

        <div className="space-y-3 mb-6">
          <h3 className="text-white font-semibold">{getTranslatedText('selectPackage')}</h3>
          {packages.map((pkg) => (
            <motion.div
              key={pkg.key}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setSelectedPackage(pkg.key)}
              className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
                selectedPackage === pkg.key
                  ? 'border-[#EE1C23] bg-[#EE1C23]/10'
                  : 'border-gray-600 hover:border-gray-500 bg-gray-800/50'
              }`}
            >
              {pkg.popular && (
                <div className="absolute -top-2 left-4 bg-[#EE1C23] text-white px-3 py-1 rounded-full text-xs font-bold">
                  {t('common.popular')}
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Clock className="w-5 h-5 text-[#EE1C23]" />
                  <div>
                    <h4 className="text-white font-semibold">
                      {getTranslatedText(`package${pkg.duration}min`)}
                    </h4>
                    <p className="text-gray-400 text-sm">
                      {getTranslatedText(`package${pkg.duration}minDesc`)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[#EE1C23] font-bold text-lg">
                    {formatPrice(pkg.price, i18n.language)}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="space-y-4">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handlePayment}
            disabled={!selectedPackage || isProcessing}
            className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-[#EE1C23] to-red-600 text-white py-3 rounded-lg font-semibold hover:from-red-600 hover:to-[#EE1C23] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CreditCard className="w-5 h-5" />
            <span>
              {isProcessing ? 'Zpracování...' : getTranslatedText('payNow')}
            </span>
          </motion.button>

          <p className="text-gray-500 text-xs text-center">
            {getTranslatedText('securePayment')}
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}